import { useCallback, useEffect, useState } from "react";

import { formatCurrency } from "helpers";
import { Pagination } from "services/search";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { getUsageEventsAPI, getUsageSummaryAPI } from "services/api/finance/usage/api";
import { initialUsageSearchForm, initialUsageSummary } from "services/api/finance/usage/consts";
import { UsageEventRow, UsageSearchFormValues, UsageSummary } from "services/api/finance/usage/types";

import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";
import { USAGE_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { Routes as FinanceRoutes } from "ui/modules/finance/navigation";

import UsageSearchFilters from "./components/UsageSearchFilters/UsageSearchFilters";
import UsageSearchResults from "./components/UsageSearchResults/UsageSearchResults";
import UsageDetailModal from "./components/UsageDetailModal/UsageDetailModal";
import { UsageSearchPageCmp, UsageSearchPageProps } from "./types";

/**
 * Izračuni i potrošnja: što je korisnik zatražio, što je dobio i koliko ga je to stajalo.
 * <para>
 * Isti ekran pokriva sve mjerene stavke iz cjenika; filtar po vrsti ga svodi na izračune.
 * </para>
 */
const UsageSearchPage: UsageSearchPageCmp = (_props: UsageSearchPageProps) => {
  const pageTitle = USAGE_SEARCH_PAGE_TITLE;

  const [searchResults, setSearchResults] =
    useState<DataWithPagination<UsageEventRow>>(initialDataWithPagination);
  const [summary, setSummary] = useState<UsageSummary>(initialUsageSummary);
  const [filters, setFilters] = useState<UsageSearchFormValues>(initialUsageSearchForm);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedUsageEventId, setSelectedUsageEventId] = useState<string | null>(null);

  const fetchSearchResult = useCallback(
    async (pagination: Pagination, values: UsageSearchFormValues) => {
      setIsLoading(true);

      try {
        // Zbroj se dohvaća uz popis, s istim filtrima, da se iznos odnosi na ono što se vidi.
        const [results, summaryResult] = await Promise.all([
          getUsageEventsAPI(pagination, values),
          getUsageSummaryAPI(values),
        ]);

        setSearchResults(results);
        setSummary(summaryResult);
      } catch {
        setSearchResults(initialDataWithPagination);
        setSummary(initialUsageSummary);
      }

      setIsLoading(false);
    },
    []
  );

  useEffect(() => {
    fetchSearchResult(initialDataWithPagination.pagination, filters);
  }, [filters, fetchSearchResult]);

  const handleSearchChange = useCallback((formValues: UsageSearchFormValues) => {
    setFilters(formValues);
  }, []);

  const handlePaginationChange = useCallback(
    (pagination: Pagination) => {
      fetchSearchResult(pagination, filters);
    },
    [fetchSearchResult, filters]
  );

  const handleRowClick = useCallback((id: string) => {
    setSelectedUsageEventId(id);
  }, []);

  const handleDetailClose = useCallback(() => {
    setSelectedUsageEventId(null);
  }, []);

  const hasResults = searchResults.pagination.totalPages !== 0;

  return (
    <div>
      <div className="bg-white rounded-t-md border border-b mb-2">
        <PageHeader>
          <PageTitle
            title={pageTitle.text}
            icon={pageTitle.icon}
            iconColor={pageTitle.iconColor}
          />
          <Breadcrumb />
        </PageHeader>
      </div>

      <div className="px-4 py-3">
        {/*
          Filteri lijevo, zbroj desno, u istom redu. `items-end` poravnava zbroj s donjim rubom
          polja, a ne s oznakom razdoblja iznad njih.
        */}
        <div className="flex items-end justify-between gap-6 mb-3">
          <UsageSearchFilters onSearchChange={handleSearchChange} />

          <div className="flex gap-8 text-right shrink-0">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                Događaja
              </p>
              <p className="text-lg font-semibold text-slate-900">{summary.count}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                Ukupno naplaćeno
              </p>
              <p className="text-lg font-semibold text-slate-900">
                {formatCurrency.format(summary.total)}
              </p>
            </div>
          </div>
        </div>

        {!isLoading && !hasResults ? (
          <NoResults message="Nema izračuna ni potrošnje u odabranom razdoblju." />
        ) : (
          <UsageSearchResults
            searchResults={searchResults}
            isLoading={isLoading}
            onPaginationChange={handlePaginationChange}
            onRowClick={handleRowClick}
          />
        )}
      </div>

      <UsageDetailModal
        show={selectedUsageEventId !== null}
        usageEventId={selectedUsageEventId}
        onClose={handleDetailClose}
      />
    </div>
  );
};

UsageSearchPage.route = FinanceRoutes.UsageSearchPageRouter;

export default UsageSearchPage;
